import { useApolloClient } from '@apollo/client'
import { ReactQuery } from 'cms-api'
import { Formik, Form, Field } from 'formik'
import Link from 'next/link'
import { rem } from 'polished'
import React from 'react'
import { ROUTE } from 'shared-constants'
import * as Yup from 'yup'
import { z } from 'zod'
import { Button } from 'components/Button'
import { Input } from 'components/Input'
import { Box, Flex } from 'components/Layout'
import { Textarea } from 'components/Textarea'
import { Toast } from 'components/Toast'
import { Text } from 'components/Typography'
import { COLOR_INTENT, COLOR_NAMES, FONT_SIZE, FONT_WEIGHT, SPACE } from 'Theme'
import { parseStrapiErrorRequiredFields } from 'utils/parseStrapiErrorFields'
import { isExpertApplicationEmailValid } from 'utils/validation'

const FORM_ERROR_MESSAGES = {
  alreadyApplied: (
    <Text>
      This email and/or firm has already applied to The Expert Trade Program.
    </Text>
  ),
  general: <Text>Something went wrong. Please try again later.</Text>,
}

export const ExpertReferralProgramForm: React.FC<{ onSubmit?: () => void }> = ({
  onSubmit,
}) => {
  const postExpertReferralProgram = ReactQuery.usePostUserReferrals({
    mutation: {
      onError: (err) => {
        const [error] = parseStrapiErrorRequiredFields(
          err.response?.data?.error
        )

        if (error.name === 'UniqueError') {
          Toast.error(FORM_ERROR_MESSAGES.alreadyApplied)
        } else {
          Toast.error(FORM_ERROR_MESSAGES.general)
        }
      },
    },
  })

  const apolloClient = useApolloClient()

  return (
    <Box
      width="100%"
      maxWidth={rem(700)}
      backgroundColor="white"
      p={SPACE.PX_32}
      borderRadius={SPACE.PX_8}
    >
      <Formik
        initialValues={{
          referrerEmail: '',
          invitationCopy: `Hey, have you heard of The Expert? It’s my new favorite place to shop for projects. They have thousands of curated products from hundreds brands, with perks like up to 20% off, tax exemption, consolidated checkout, and proactive logistics management—all designed to save you time without compromising on the brands you love. Join their trade program and, once accepted, you’ll receive a code for $200 off your first order!`,
        }}
        validationSchema={Yup.object().shape({
          referrerEmail: Yup.string()
            .email('Invalid email')
            .trim()
            .label('Email')
            .test(
              'valid-email',
              '',
              // eslint-disable-next-line func-names
              async function (value) {
                const { path, createError } = this

                const isValidEmail = z.string().email().safeParse(value).success
                if (!isValidEmail) {
                  return createError({
                    path,
                    message: 'Invalid email format',
                  })
                }

                const doesExists = await isExpertApplicationEmailValid({
                  email: value,
                  apolloClient,
                  omitRejected: true,
                })

                if (!doesExists) {
                  return createError({
                    path,
                    message: 'This user already has an account',
                  })
                }

                return true
              }
            )
            .required(),
          invitationCopy: Yup.string().max(
            1000,
            'Maximum 1000 characters allowed'
          ),
        })}
        onSubmit={async (values, { resetForm }) => {
          if (values) {
            const res = await postExpertReferralProgram.mutateAsync({
              data: {
                data: {
                  refereeEmail: values.referrerEmail.trim(),
                  invitationCopy: values.invitationCopy,
                },
              },
            })

            // @ts-ignore
            if (res?.status === 'success') {
              Toast.success('Your invitation was successfully sent')
              resetForm()
              onSubmit?.()
            }
          }
        }}
      >
        {({ handleSubmit, isSubmitting, setFieldValue }) => (
          <Form onSubmit={handleSubmit}>
            <Text
              fontSize={FONT_SIZE.PX_22}
              color={COLOR_NAMES.PINEY_LAKE}
              fontWeight={FONT_WEIGHT.MEDIUM}
              as="h2"
            >
              Refer a Trade Firm
            </Text>
            <Text
              fontSize={FONT_SIZE.PX_16}
              color={COLOR_NAMES.EEIRE_BLACK}
              fontStyle="italic"
            >
              Know a fellow designer who would love shopping on The Expert? Give
              them a $200 gift and we’ll give you $200 after their first
              purchase.
            </Text>
            <Flex flexDirection="column" mt={SPACE.PX_40}>
              <Field name="referrerEmail">
                {({ field, meta }) => (
                  <Input
                    label="Email"
                    autocomplete={false}
                    isError={meta.touched && meta.error !== undefined}
                    hint={meta.touched && meta.error ? meta.error : null}
                    hasHint
                    placeholder="Send to"
                    {...field}
                  />
                )}
              </Field>
              <Field name="invitationCopy">
                {({ field, meta }) => (
                  <Textarea
                    {...field}
                    hint={meta.touched && meta.error ? meta.error : null}
                    isError={meta.error}
                    hasHint
                    rows="7"
                    onPaste={(value) => {
                      setFieldValue(field.name, value)
                    }}
                  />
                )}
              </Field>
              <Flex display="flex" justifyContent="flex-start">
                <Button
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                  type="submit"
                  label="Invite Trade friends"
                  margin={0}
                >
                  Invite Trade Friends
                </Button>
              </Flex>
              <Text
                fontSize={FONT_SIZE.PX_10}
                color={COLOR_INTENT.GRAY_60}
                mt={SPACE.PX_6}
              >
                Referrals are subject to our{' '}
                <Link href={ROUTE.PRIVACY_POLICY()}>Privacy Notice</Link> and
                the Referral Program Terms set forth in the{' '}
                <Link href={ROUTE.TRADE_PROGRAM_AGREEMENT()}>
                  Trade Program Agreement
                </Link>
                . By sending a referral, you acknowledge you have read,
                understand and agree to our Privacy Notice and Trade Program
                Agreement. Exclusions apply.
              </Text>
            </Flex>
          </Form>
        )}
      </Formik>
    </Box>
  )
}
